import InputService, { Keys, KeyChord } from "../input/inputservice";
import Stage from "../stage/stage";
import SceneObject from "./sceneobject";

/*
By default, Placement is in continuous mode and uses your speed formula as before. To switch modes during play, press Shift+X, or call placement.set_mode("step") explicitly.

In continuous mode:
AWSD, arrows, and up/down all act while held,
the per-frame step is speed * increment * delta_time * modifier,
Shift makes everything 4x faster, Ctrl makes it 4x slower.

In step mode:
each tap of AWSD nudges by exactly step_size_move units,
each tap of Left/Right rotates by exactly step_size_rotate_degrees,
each tap of Up/Down scales by exactly step_size_scale,
Shift and Ctrl still multiply the step size for coarse or fine adjustments.
*/
export type PlacementMode = "continuous" | "step";

export default class Placement {
  // Resolve input lazily so SceneObjects can be constructed while the Stage
  // singleton is still being built, such as StickerService-owned Video.
  private _input: InputService | null = null;
  get input(): InputService {
    if (!this._input) this._input = Stage.instance().input_service;
    return this._input;
  }
  scene_object: SceneObject;

  move_increment: number = 0.1;
  move_speed: number = 1;
  rotate_increment: number = 0.1;
  rotate_speed: number = 10;
  scale_increment: number = 0.1;
  scale_speed: number = 1;

  mode: PlacementMode = "continuous";

  fast_modifier_key: string = Keys.Shift;
  slow_modifier_key: string = Keys.Ctrl;
  fast_multiplier: number = 4.0;
  slow_multiplier: number = 0.25;

  step_size_move: number = 0.1;
  step_size_rotate_degrees: number = 5.0;
  step_size_scale: number = 0.05;

  toggle_mode_chord: KeyChord = { required: [Keys.Shift, Keys.X] };

  constructor(scene_object: SceneObject) {
    this.scene_object = scene_object;
  }

  set_speed(
    move_increment: number,
    move_speed: number,
    rotate_increment: number,
    rotate_speed: number,
    scale_increment: number,
    scale_speed: number
  ): void {
    this.move_increment = move_increment;
    this.move_speed = move_speed;
    this.rotate_increment = rotate_increment;
    this.rotate_speed = rotate_speed;
    this.scale_increment = scale_increment;
    this.scale_speed = scale_speed;
  }

  set_mode(mode: PlacementMode): void {
    this.mode = mode;
  }

  set_step_sizes(move: number, rotate_degrees: number, scale: number): void {
    this.step_size_move = move;
    this.step_size_rotate_degrees = rotate_degrees;
    this.step_size_scale = scale;
  }

  set_modifiers(
    fast_key: string,
    slow_key: string,
    fast_multiplier: number,
    slow_multiplier: number
  ): void {
    this.fast_modifier_key = fast_key;
    this.slow_modifier_key = slow_key;
    this.fast_multiplier = fast_multiplier;
    this.slow_multiplier = slow_multiplier;
  }
  
  tick(delta_time: number): void 
  {
    const transform = this.scene_object.transform;

    if (this.input.was_chord_pressed(this.toggle_mode_chord)) {
      this.mode = this.mode === "continuous" ? "step" : "continuous";
    }

    const modifier = this.get_modifier_multiplier();

    let dx = 0;
    let dy = 0;
    let dz = 0;

    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.D)) dx += 1;
      if (this.input.is_key_down(Keys.A)) dx -= 1;
      if (this.input.is_key_down(Keys.W)) dz -= 1;
      if (this.input.is_key_down(Keys.S)) dz += 1;
      if (this.input.is_key_down(Keys.PageUp))   dy += 1;
      if (this.input.is_key_down(Keys.PageDown)) dy -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.D)) dx += 1;
      if (this.input.was_key_pressed(Keys.A)) dx -= 1;
      if (this.input.was_key_pressed(Keys.W)) dz -= 1;
      if (this.input.was_key_pressed(Keys.S)) dz += 1;
      if (this.input.was_key_pressed(Keys.PageUp))   dy += 1;
      if (this.input.was_key_pressed(Keys.PageDown)) dy -= 1;
    }

    if (dx !== 0 || dy !== 0 || dz !== 0) {
      let move_step: number;
      if (this.mode === "continuous") {
        move_step =
          this.move_speed * this.move_increment * delta_time * modifier;
      } else {
        move_step = this.step_size_move * modifier;
      }
      transform.translate(dx * move_step, dy * move_step, dz * move_step);
      this.log_transform_change("move");
    }

    let yaw_dir = 0;
    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.Right)) yaw_dir += 1;
      if (this.input.is_key_down(Keys.Left)) yaw_dir -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.Right)) yaw_dir += 1;
      if (this.input.was_key_pressed(Keys.Left)) yaw_dir -= 1;
    }

    if (yaw_dir !== 0) {
      let degrees_step: number;
      if (this.mode === "continuous") {
        degrees_step =
          this.rotate_speed * this.rotate_increment * delta_time * modifier;
      } else {
        degrees_step = this.step_size_rotate_degrees * modifier;
      }
      const radians_step = degrees_step * (Math.PI / 180);
      transform.rotate(0, yaw_dir * radians_step, 0);
       this.log_transform_change("rotate");
    }

    let scale_dir = 0;
    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.Up)) scale_dir += 1;
      if (this.input.is_key_down(Keys.Down)) scale_dir -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.Up)) scale_dir += 1;
      if (this.input.was_key_pressed(Keys.Down)) scale_dir -= 1;
    }

    if (scale_dir !== 0) {
      let scale_step: number;
      if (this.mode === "continuous") {
        scale_step =
          this.scale_speed * this.scale_increment * delta_time * modifier;
      } else {
        scale_step = this.step_size_scale * modifier;
      }

      const factor = 1.0 + scale_dir * scale_step;
      transform.scale[0] *= factor;
      transform.scale[1] *= factor;
      transform.scale[2] *= factor;
      this.log_transform_change("scale");
    }

    if (
      dx !== 0 ||
      dy !== 0 ||
      dz !== 0 ||
      yaw_dir !== 0 ||
      scale_dir !== 0
    ) {
      transform.update_matrix();
    }
  }
    private get_modifier_multiplier(): number {
    let mult = 1.0;

    if (this.input.is_key_down(this.fast_modifier_key)) {
      mult *= this.fast_multiplier;
    }

    if (this.input.is_key_down(this.slow_modifier_key)) {
      mult *= this.slow_multiplier;
    }

    return mult;
  }

    private log_transform_change(kind: "move" | "rotate" | "scale"): void {
    const t = this.scene_object.transform;
    const key = this.scene_object.key;

    if (kind === "move") {
      console.log(
        `${key} move ${t.position[0]},${t.position[1]},${t.position[2]}`
      );
    } else if (kind === "rotate") {
      const deg_x = t.rotation[0] * (180 / Math.PI);
      const deg_y = t.rotation[1] * (180 / Math.PI);
      const deg_z = t.rotation[2] * (180 / Math.PI);
      console.log(`${key} rotate ${deg_x},${deg_y},${deg_z}`);
    } else if (kind === "scale") {
      console.log(
        `${key} scale ${t.scale[0]},${t.scale[1]},${t.scale[2]}`
      );
    }
  }
}